// firestoreservice.js — Toutes les interactions Firestore SAFI Academia
import {
  doc, getDoc, setDoc, updateDoc, collection,
  getDocs, query, orderBy, serverTimestamp, increment
} from 'firebase/firestore';
import { db } from './Firebase';

// ─── PROGRESSION ─────────────────────────────────────────────────────────────

/**
 * Enregistre le résultat d'un quiz dans Firestore
 * et met à jour le document étudiant si meilleur score.
 */
export async function saveQuizResult({ uid, quizId, type, niveau, titre, score, total, answers, threshold }) {
  const pct    = Math.round((score / total) * 100);
  const passed = pct >= threshold;
  const ref    = doc(db, 'etudiants', uid, 'progression', quizId);

  // Vérifie s'il existe déjà un meilleur score
  const existing = await getDoc(ref);
  const bestPct  = existing.exists() ? Math.max(existing.data().bestPct ?? 0, pct) : pct;
  const attempts = existing.exists() ? (existing.data().attempts ?? 0) + 1 : 1;

  await setDoc(ref, {
    quizId, type, niveau, titre, score, total, pct, passed, threshold,
    answers, attempts, bestPct,
    completedAt: serverTimestamp(),
  });

  return { pct, passed, bestPct };
}

/**
 * Récupère toute la progression d'un étudiant (tous ses quiz)
 */
export async function getProgression(uid) {
  const snap = await getDocs(collection(db, 'etudiants', uid, 'progression'));
  const result = {};
  snap.forEach(d => { result[d.id] = d.data(); });
  return result; // { [quizId]: { pct, passed, bestPct, ... } }
}

/**
 * Vérifie si un quiz est débloqué selon les règles :
 * - quiz de cours : le cours précédent doit être passé
 * - quiz de module : tous les cours du module doivent être passés
 * - examen final : tous les modules doivent être passés
 */
export function isUnlocked({ quizId, type, moduleCoursesIds, allModulesIds, progression }) {
  if (type === 'course') {
    // Récupère l'index parmi tous les cours
    // Le premier cours est toujours débloqué
    const idx = moduleCoursesIds.indexOf(quizId);
    if (idx === 0) return true;
    const prev = moduleCoursesIds[idx - 1];
    return (progression[prev]?.bestPct ?? 0) >= 50;
  }
  if (type === 'module') {
    // Tous les cours du module doivent être passés
    return moduleCoursesIds.every(id => (progression[id]?.bestPct ?? 0) >= 50);
  }
  if (type === 'final') {
    // Tous les quiz de modules doivent être passés
    return allModulesIds.every(id => (progression[id]?.bestPct ?? 0) >= 50);
  }
  return false;
}

// ─── TEMPS D'ÉTUDE ───────────────────────────────────────────────────────────

/**
 * Enregistre du temps d'étude pour aujourd'hui
 */
export async function logStudyTime(uid, minutes) {
  const today = new Date().toISOString().split('T')[0]; // "2026-06-03"
  const ref   = doc(db, 'etudiants', uid, 'temps', today);
  const snap  = await getDoc(ref);
  if (snap.exists()) {
    await updateDoc(ref, {
      minutes:  increment(minutes),
      sessions: increment(1),
    });
  } else {
    await setDoc(ref, { date: today, minutes, sessions: 1 });
  }
}

/**
 * Récupère le temps d'étude des 7 derniers jours
 */
export async function getStudyTime(uid) {
  const snap = await getDocs(
    query(collection(db, 'etudiants', uid, 'temps'), orderBy('date', 'desc'))
  );
  const days = [];
  snap.forEach(d => days.push(d.data()));
  return days.slice(0, 7).reverse(); // 7 derniers jours, ordre chronologique
}

// ─── COURS & QUIZ depuis Firestore ───────────────────────────────────────────

/**
 * Récupère les cours d'un niveau depuis Firestore
 */
export async function getCoursByNiveau(niveau) {
  const snap = await getDocs(
    query(collection(db, 'cours', niveau, 'liste'), orderBy('ordre'))
  );
  const result = {};
  snap.forEach(d => {
    const data = d.data();
    if (!result[data.module]) result[data.module] = [];
    result[data.module].push({ id: d.id, ...data });
  });
  return result; // { Grammar: [...], Conjugating: [...], ... }
}

/**
 * Récupère un quiz depuis Firestore
 */
export async function getQuiz(niveau, quizId) {
  const snap = await getDoc(doc(db, 'quiz', niveau, 'liste', quizId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/**
 * Récupère tous les quiz d'un niveau
 */
export async function getAllQuiz(niveau) {
  const snap = await getDocs(collection(db, 'quiz', niveau, 'liste'));
  const result = {};
  snap.forEach(d => { result[d.id] = d.data(); });
  return result;
}

// ─── ÉTUDIANT ────────────────────────────────────────────────────────────────

/**
 * Met à jour le niveau de l'étudiant (appelé par l'admin ou après examen)
 */
export async function updateNiveau(uid, newNiveau) {
  await updateDoc(doc(db, 'etudiants', uid), {
    niveau: newNiveau,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Récupère les infos d'un étudiant
 */
export async function getEtudiant(uid) {
  const snap = await getDoc(doc(db, 'etudiants', uid));
  return snap.exists() ? snap.data() : null;
}